import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import { Heart, Coffee, Globe, Rocket, Users, GraduationCap } from "lucide-react";

export const Route = createFileRoute("/careers")({
  component: CareersPage,
  head: () => ({
    meta: [
      { title: "Careers at CoderLity — Join our team" },
      { name: "description", content: "Open roles at CoderLity — remote-first engineering, design, marketing and support positions with great benefits." },
      { property: "og:title", content: "Careers at CoderLity" },
      { property: "og:description", content: "Remote-first roles building software, marketing and hosting for growing businesses worldwide." },
      { property: "og:url", content: "/careers" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/careers" }],
  }),
});

const perks = [
  { icon: Globe, title: "Remote-first", body: "Work from anywhere. Optional hubs in Dhaka, Riyadh and Dubai." },
  { icon: Heart, title: "Health & wellness", body: "Health insurance, mental health support and gym stipend." },
  { icon: GraduationCap, title: "Learning budget", body: "$1,500/yr for courses, books and conferences." },
  { icon: Rocket, title: "Equity & bonuses", body: "Meaningful equity plus performance bonuses." },
  { icon: Coffee, title: "Flexible hours", body: "Core overlap 3 hours/day, the rest is yours." },
  { icon: Users, title: "Team offsites", body: "Twice-yearly retreats to reconnect in person." },
];

const openings = [
  { role: "Senior Full-Stack Engineer", team: "Engineering", location: "Remote" },
  { role: "Product Designer", team: "Design", location: "Remote" },
  { role: "Performance Marketing Lead", team: "Marketing", location: "Remote" },
  { role: "DevOps / SRE", team: "Infrastructure", location: "Remote" },
  { role: "Customer Success Manager", team: "Support", location: "Remote" },
];

function CareersPage() {
  return (
    <PageShell>
      <PageHero eyebrow="Careers" title="Build the future of business software" sub="We're a small, senior team shipping products used by thousands of businesses. Come help us go bigger." />

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight">Why CoderLity</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {perks.map((p) => (
              <div key={p.title} className="rounded-2xl border border-border bg-card p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight">Open positions</h2>
          <div className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card">
            {openings.map((o) => (
              <div key={o.role} className="flex flex-wrap items-center justify-between gap-4 p-5">
                <div>
                  <div className="font-semibold">{o.role}</div>
                  <div className="text-sm text-muted-foreground">{o.team} · {o.location}</div>
                </div>
                <Link to="/contact" className="text-sm font-semibold text-primary hover:underline">Apply →</Link>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">Don't see your role? <Link to="/contact" className="text-primary hover:underline">Send us your CV</Link> anyway.</p>
        </div>
      </section>

      <CTABanner />
    </PageShell>
  );
}
